import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import "./SalaryGuide.css";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const SALARY_GUIDE_URL = API_BASE
  ? `${API_BASE.replace(/\/api$/, "")}/api/salary-guide`
  : "/salary-guide";

function fmtLPA(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `₹${Math.round(n)} LPA`;
}

function norm(value) {
  return (value ?? "").toString().trim().toLowerCase().replace(/\s+/g, " ");
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function AutoInput({
  value,
  onChange,
  onSelect,
  placeholder,
  icon,
  suggestions,
  showDropdown,
  onFocus,
  onBlur,
}) {
  const wrapRef = useRef(null);

  return (
    <div className="sal-ac-wrap" ref={wrapRef}>
      <div className="sal-search-field">
        <span className="sal-search-field__icon">{icon}</span>
        <input
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          autoComplete="off"
          spellCheck={false}
        />
        {value && (
          <button
            className="sal-search-clear"
            type="button"
            onMouseDown={e => {
              e.preventDefault();
              onChange("");
            }}
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && suggestions.length > 0 && createPortal(
        <div
          className="sal-ac-dropdown"
          style={{
            position: "fixed",
            top: (wrapRef.current?.getBoundingClientRect().bottom ?? 0) + 8,
            left: wrapRef.current?.getBoundingClientRect().left ?? 0,
            width: wrapRef.current?.getBoundingClientRect().width ?? 0,
            zIndex: 99999,
          }}
        >
          {suggestions.map((s, idx) => (
            <button
              key={`${s}-${idx}`}
              className="sal-ac-item"
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                onSelect(s);
              }}
            >
              <span className="sal-ac-item__icon">🔍</span>
              <span className="sal-ac-item__text">{s}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function CityBar({ city }) {
  const pct = Math.max(12, Math.min(100, (Number(city.avgLPA) || 0) * 4));
  return (
    <div className="sal-city-row">
      <span className="sal-city-row__name">{city.city}</span>
      <div className="sal-city-row__track">
        <div className="sal-city-row__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="sal-city-row__meta">
        {fmtLPA(city.avgLPA)}
      </span>
    </div>
  );
}

function RoleCard({ entry, activeCity, onClick, index }) {
  const cityMatch = activeCity
    ? entry.salaryByCity?.find(c => norm(c.city) === norm(activeCity))
    : null;

  const displayAvg = cityMatch?.avgLPA ?? entry.avgSalaryLPA;
  const ranges = [entry.minSalaryLPA, displayAvg, entry.maxSalaryLPA].filter(v => Number(v) > 0);
  const cityCount = safeArray(entry.salaryByCity).length;
  const topSkills = safeArray(entry.keySkills).slice(0, 3);

  return (
    <article
      className="sal-card"
      style={{ animationDelay: `${index * 0.03}s`, "--cat-color": "var(--sal-prime)" }}
      onClick={onClick}
    >
      <div className="sal-card__head">
        <div className="sal-card__icon-wrap">💼</div>
        <div style={{ display: "grid", justifyItems: "end", gap: 4 }}>
          <span className="sal-card__cat">{entry.category || "Live salary"}</span>
          <span className="sal-role-note">{entry.jobCount} job posts</span>
        </div>
      </div>

      <div className="sal-card__role">{entry.role}</div>
      <div className="sal-role-note">
        {cityMatch ? `Filtered to ${activeCity}` : "Salary built from live job posts"}
      </div>

      <div className="sal-card__avg-row">
        {displayAvg ? (
          <>
            <span className="sal-card__avg">{fmtLPA(displayAvg)}</span>
            <span className="sal-card__avg-lbl">avg / year</span>
          </>
        ) : (
          <span className="sal-card__no-data">No salary data yet</span>
        )}
      </div>

      {ranges.length >= 2 && (
        <div className="sal-card__range">
          <span className="sal-card__rmin">{fmtLPA(entry.minSalaryLPA)}</span>
          <div className="sal-card__rtrack">
            <div className="sal-card__rfill" />
            {displayAvg && <div className="sal-card__rdot" style={{ left: "50%" }} />}
          </div>
          <span className="sal-card__rmax">{fmtLPA(entry.maxSalaryLPA)}</span>
        </div>
      )}

      {cityCount > 0 && (
        <div className="sal-cbd">
          <div className="sal-cbd__hd">
            <span className="sal-cbd__title">Top cities</span>
            <span className="sal-city-jobcount">{cityCount} cities</span>
          </div>
          {safeArray(entry.salaryByCity).slice(0, 3).map(city => (
            <CityBar key={`${entry.role}-${city.city}`} city={city} />
          ))}
        </div>
      )}

      <div className="sal-card__foot">
        <span className="sal-card__jobs">
          {topSkills.length ? topSkills.join(" · ") : "Click to inspect roles"}
        </span>
        <span className="sal-card__cta">Details →</span>
      </div>
    </article>
  );
}

function RoleModal({ entry, onClose, activeCity }) {
  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!entry) return null;

  const cityMatch = activeCity
    ? entry.salaryByCity?.find(c => norm(c.city) === norm(activeCity))
    : null;

  return createPortal(
    <div className="sal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sal-modal" role="dialog" aria-modal="true">
        <button className="sal-modal__close" type="button" onClick={onClose}>×</button>

        <div className="sal-modal__head">
          <div className="sal-modal__icon-wrap">💼</div>
          <div>
            <div className="sal-modal__title">{entry.role}</div>
            <div className="sal-modal__desc">{entry.jobCount} active posts powering this guide</div>
            {cityMatch && (
              <div className="sal-modal__city-pill">
                📍 {activeCity}: <strong>{fmtLPA(cityMatch.avgLPA)}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="sal-modal__summary">
          <div className="sal-sumbox">
            <div className="sal-sumbox__label">Minimum</div>
            <div className="sal-sumbox__val">{fmtLPA(entry.minSalaryLPA)}</div>
            <div className="sal-sumbox__note">across live job posts</div>
          </div>
          <div className="sal-sumbox sal-sumbox--avg">
            <div className="sal-sumbox__label">{cityMatch ? `Average in ${activeCity}` : "Average"}</div>
            <div className="sal-sumbox__val">{fmtLPA(cityMatch?.avgLPA ?? entry.avgSalaryLPA)}</div>
            <div className="sal-sumbox__note">market average</div>
          </div>
          <div className="sal-sumbox">
            <div className="sal-sumbox__label">Maximum</div>
            <div className="sal-sumbox__val">{fmtLPA(entry.maxSalaryLPA)}</div>
            <div className="sal-sumbox__note">upper offer band</div>
          </div>
        </div>

        {Object.keys(entry.salaryByExp || {}).length > 0 ? (
          <div className="sal-modal__section">
            <div className="sal-modal__sec-title">Salary by experience</div>
            <div className="sal-expbars">
              {Object.entries(entry.salaryByExp || {}).map(([key, val]) => {
                const label = {
                  fresher: "Fresher",
                  mid: "Mid",
                  senior: "Senior",
                  lead: "Lead",
                }[key] || key;
                return (
                  <div key={key} className="sal-exp-row">
                    <span className="sal-exp-row__label">{label}</span>
                    <div className="sal-exp-row__track">
                      <div className="sal-exp-row__fill" style={{ width: `${Math.max(20, Math.min(100, Number(val || 0) * 4))}%` }} />
                    </div>
                    <span className="sal-exp-row__val">{fmtLPA(val)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {safeArray(entry.salaryByCity).length > 0 && (
          <div className="sal-modal__section">
            <div className="sal-modal__sec-title">Salary by city</div>
            <div className="sal-city-grid">
              {entry.salaryByCity.map(city => (
                <div
                  key={`${entry.role}-${city.city}`}
                  className={`sal-city-card${activeCity && norm(city.city) === norm(activeCity) ? " sal-city-card--active" : ""}`}
                >
                  <div className="sal-city-card__name">{city.city}</div>
                  <div className="sal-city-card__val">{fmtLPA(city.avgLPA)}</div>
                  <div className="sal-city-card__meta">
                    min {fmtLPA(city.minLPA)} · max {fmtLPA(city.maxLPA)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {safeArray(entry.keySkills).length > 0 && (
          <div className="sal-modal__section">
            <div className="sal-modal__sec-title">Skills in demand</div>
            <div className="sal-skill-row">
              {entry.keySkills.map(skill => (
                <span key={skill} className="sal-skill-chip">{skill}</span>
              ))}
            </div>
          </div>
        )}

        {safeArray(entry.recentJobs).length > 0 && (
          <div className="sal-modal__section">
            <div className="sal-modal__sec-title">Recent job posts</div>
            <div className="sal-job-list">
              {entry.recentJobs.map((job, idx) => (
                <div key={`${job.title}-${idx}`} className="sal-job-pill">
                  <div>
                    <div className="sal-job-pill__title">{job.title}</div>
                    <div className="sal-job-pill__meta">{job.company} · {job.location}</div>
                  </div>
                  <div className="sal-job-pill__salary">
                    {fmtLPA(job.salaryMin)} - {fmtLPA(job.salaryMax)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function SalaryGuideLive() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchParams] = useSearchParams();
  const [roleInput, setRoleInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [roleOpen, setRoleOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [sort, setSort] = useState("avg-desc");
  const [locationFilter, setLocationFilter] = useState("all");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(SALARY_GUIDE_URL);
        const json = await res.json();
        if (!json.success) throw new Error(json.error || "Failed to load salary data");
        setEntries(json.data || []);

        const role = searchParams.get("role");
        const city = searchParams.get("city") || searchParams.get("location");
        if (role) setRoleInput(role);
        if (city) setCityInput(city);
        if (city) setLocationFilter(city);
      } catch (err) {
        setError(err.message || "Could not load salary guide");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [searchParams]);

  const allRoles = useMemo(
    () => entries.map(e => e.role).filter(Boolean),
    [entries]
  );

  const allCities = useMemo(() => {
    const cities = new Set();
    entries.forEach(entry => {
      safeArray(entry.salaryByCity).forEach(city => {
        if (city.city) cities.add(city.city);
      });
    });
    return [...cities].sort();
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const query = norm(roleInput);
    const city = locationFilter !== "all" ? norm(locationFilter) : "";

    return entries
      .filter(entry => {
        if (query && !norm(entry.role).includes(query)) return false;
        if (city && entry.salaryByCity?.length) {
          return entry.salaryByCity.some(c => norm(c.city) === city);
        }
        return true;
      })
      .sort((a, b) => {
        const aVal = city
          ? a.salaryByCity?.find(c => norm(c.city) === city)?.avgLPA ?? a.avgSalaryLPA ?? 0
          : a.avgSalaryLPA ?? 0;
        const bVal = city
          ? b.salaryByCity?.find(c => norm(c.city) === city)?.avgLPA ?? b.avgSalaryLPA ?? 0
          : b.avgSalaryLPA ?? 0;
        if (sort === "avg-asc") return aVal - bVal;
        if (sort === "avg-desc") return bVal - aVal;
        if (sort === "posts-desc") return (b.jobCount || 0) - (a.jobCount || 0);
        return (a.role || "").localeCompare(b.role || "");
      });
  }, [entries, roleInput, locationFilter, sort]);

  const withData = filteredEntries.filter(e => (e.avgSalaryLPA ?? 0) > 0);
  const quickRoles = allRoles.length > 0
    ? allRoles.slice(0, 6)
    : [
        "Software Engineer",
        "Frontend Developer",
        "Backend Developer",
        "Data Scientist",
        "Product Manager",
        "UI/UX Designer",
      ];

  const activeCity = locationFilter !== "all" ? locationFilter : "";

  return (
    <div className="sal-root">
      <section className="sal-hero">
        <div className="sal-hero__orb sal-hero__orb--1" />
        <div className="sal-hero__orb sal-hero__orb--2" />
        <div className="sal-hero__orb sal-hero__orb--3" />

        <div className="sal-hero__inner">
          <div className="sal-hero__badge">
            <span className="sal-hero__badge-dot" />
            <span>Live salary guide from job posts</span>
          </div>

          <h1 className="sal-hero__title">
            Know the salary range for every role, city, and experience level.
          </h1>

          <p className="sal-hero__sub">
            We read live job postings in the database and calculate average, minimum, and maximum pay by job title and city.
          </p>

          <div className="sal-hero__stats">
            <div className="sal-stat">
              <span className="sal-stat__value">{entries.length}</span>
              <span className="sal-stat__label">Roles tracked</span>
            </div>
            <div className="sal-stat">
              <span className="sal-stat__value">{allCities.length}</span>
              <span className="sal-stat__label">Cities covered</span>
            </div>
            <div className="sal-stat">
              <span className="sal-stat__value">{entries.reduce((sum, e) => sum + (e.jobCount || 0), 0)}</span>
              <span className="sal-stat__label">Active job posts</span>
            </div>
          </div>

          <div className="sal-searchbar">
            <AutoInput
              value={roleInput}
              onChange={v => {
                setRoleInput(v);
                setRoleOpen(true);
              }}
              onSelect={v => {
                setRoleInput(v);
                setRoleOpen(false);
              }}
              placeholder="Search by job title"
              icon="💼"
              suggestions={allRoles.filter(role => norm(role).includes(norm(roleInput))).slice(0, 8)}
              showDropdown={roleOpen && roleInput.trim().length > 0}
              onFocus={() => setRoleOpen(true)}
              onBlur={() => setTimeout(() => setRoleOpen(false), 150)}
            />

            <div className="sal-search-sep" />

            <AutoInput
              value={cityInput}
              onChange={v => {
                setCityInput(v);
                setLocationFilter(v || "all");
                setCityOpen(true);
              }}
              onSelect={v => {
                setCityInput(v);
                setLocationFilter(v);
                setCityOpen(false);
              }}
              placeholder="Filter by city"
              icon="📍"
              suggestions={allCities.filter(city => norm(city).includes(norm(cityInput))).slice(0, 8)}
              showDropdown={cityOpen && cityInput.trim().length > 0}
              onFocus={() => setCityOpen(true)}
              onBlur={() => setTimeout(() => setCityOpen(false), 150)}
            />

            <button
              className="sal-search-btn"
              type="button"
              onClick={() => {
                const match = entries.find(entry => norm(entry.role) === norm(roleInput));
                if (match) setSelected(match);
              }}
            >
              <span>Inspect</span>
              <span className="sal-search-btn__arrow">→</span>
            </button>
          </div>

          <div className="sal-chips">
            {quickRoles.map(role => (
              <button
                key={role}
                type="button"
                className={`sal-chip${norm(roleInput) === norm(role) ? " sal-chip--active" : ""}`}
                onClick={() => setRoleInput(role)}
              >
                {role}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="sal-results">
        <div className="sal-filterbar" style={{ position: "sticky", top: 0, zIndex: 15 }}>
          <div className="sal-fb-row">
            <div className={`sal-fb-field sal-fb-field--select${activeCity ? " sal-fb-field--active" : ""}`}>
              <span className="sal-fb-field__pin">📍</span>
              <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)}>
                <option value="all">All Cities</option>
                {allCities.map(city => <option key={city} value={city}>{city}</option>)}
              </select>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sal-fb-field__chev"><path d="m6 9 6 6 6-6" /></svg>
            </div>

            <div className="sal-fb-field sal-fb-field--select">
              <span className="sal-fb-field__icon">↕</span>
              <select value={sort} onChange={e => setSort(e.target.value)}>
                <option value="avg-desc">Highest salary</option>
                <option value="avg-asc">Lowest salary</option>
                <option value="posts-desc">Most job posts</option>
                <option value="role">Role A-Z</option>
              </select>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sal-fb-field__chev"><path d="m6 9 6 6 6-6" /></svg>
            </div>

            <div className="sal-fb-spacer" />
            <div className="sal-fb-count">
              <strong>{withData.length}</strong> roles shown
            </div>
          </div>
        </div>

        {loading && (
          <div className="sal-loading">
            <div className="sal-spinner" />
            <div className="sal-spinner-text">Loading live job-post salary data...</div>
          </div>
        )}

        {!loading && error && (
          <div className="sal-error">
            <div style={{ fontSize: "2rem", marginBottom: 8 }}>⚠️</div>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && filteredEntries.length === 0 && (
          <div className="sal-empty">
            <div className="sal-empty__icon">🔍</div>
            <div className="sal-empty__title">No salary data matches your filter</div>
            <div className="sal-empty__sub">Try a different role or clear the city filter.</div>
          </div>
        )}

        {!loading && !error && filteredEntries.length > 0 && (
          <>
            <div className="sal-sec-hd">
              <span className="sal-sec-hd__dot" />
              <span>
                Live salary guide {roleInput ? `for ${roleInput}` : "based on job posts"}
                {activeCity ? ` · filtered to ${activeCity}` : ""}
              </span>
              <div className="sal-sec-hd__line" />
            </div>

            <div className="sal-grid">
              {filteredEntries.map((entry, idx) => (
                <RoleCard
                  key={entry.id || `${entry.role}-${idx}`}
                  entry={entry}
                  activeCity={activeCity}
                  index={idx}
                  onClick={() => setSelected(entry)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {selected && (
        <RoleModal
          entry={selected}
          onClose={() => setSelected(null)}
          activeCity={activeCity}
        />
      )}
    </div>
  );
}
